import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, projectFirestore } from "../firebase/config";
import { normalizeMenuTabsBlocked } from "../constants/dashboardTabPermissions";

const normalizeMembershipRole = (role) => {
  const normalized = (role || "staff").toString().toLowerCase();
  if (["owner", "admin", "staff", "viewer", "partner"].includes(normalized)) {
    return normalized;
  }
  return "staff";
};

const normalizeMembershipStatus = (status) => {
  const normalized = (status || "active").toString().toLowerCase();
  return normalized === "suspended" ? "suspended" : "active";
};

const resolveFromFirestore = async (authUid) => {
  const membershipRef = doc(projectFirestore, "accountMemberships", authUid);
  const membershipSnap = await getDoc(membershipRef);

  if (!membershipSnap.exists()) {
    return {
      authUid,
      ownerUid: authUid,
      role: "owner",
      status: "active",
      isMember: false,
      member: null,
      menuTabsBlocked: [],
    };
  }

  const membership = membershipSnap.data() || {};
  const ownerUid = membership.ownerUid || authUid;
  const role = normalizeMembershipRole(membership.role || "staff");
  const status = normalizeMembershipStatus(membership.status || "active");
  const isMember = ownerUid !== authUid;

  let member = null;
  let menuTabsBlocked = normalizeMenuTabsBlocked(membership.menuTabsBlocked);
  if (isMember) {
    const memberRef = doc(
      projectFirestore,
      "users",
      ownerUid,
      "accountMembers",
      authUid
    );
    const memberSnap = await getDoc(memberRef);
    if (memberSnap.exists()) {
      member = memberSnap.data() || {};
      menuTabsBlocked = normalizeMenuTabsBlocked(member.menuTabsBlocked);
    }
  }

  return {
    authUid,
    ownerUid,
    role,
    status,
    isMember,
    member,
    menuTabsBlocked,
  };
};

/** Callable cold starts can stall sign-in for many seconds; prefer fast Firestore fallback. */
const DASHBOARD_CONTEXT_CALLABLE_MS = 2500;

export const resolveAccountContext = async (authUser) => {
  if (!authUser?.uid) {
    return null;
  }

  // The local harness seeds the authoritative membership documents directly.
  // Avoid waiting for a large Functions emulator cold start on every login;
  // production still prefers the callable boundary below.
  if (process.env.VUE_APP_USE_EMULATORS === "true") {
    try {
      return await resolveFromFirestore(authUser.uid);
    } catch (error) {
      console.warn(
        "[accountContext] Emulator Firestore lookup failed, defaulting to authenticated user context.",
        error,
      );
      return {
        authUid: authUser.uid,
        ownerUid: authUser.uid,
        role: "owner",
        status: "active",
        isMember: false,
        member: null,
        menuTabsBlocked: [],
      };
    }
  }

  try {
    const functions = getFunctions(app, "us-central1");
    const getContext = httpsCallable(functions, "getDashboardAccountContextV2");
    const result = await Promise.race([
      getContext(),
      new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("getDashboardAccountContextV2 timed out"));
        }, DASHBOARD_CONTEXT_CALLABLE_MS);
      }),
    ]);
    const contextData = result?.data?.context;

    if (result?.data?.success && contextData?.ownerUid) {
      const member = result?.data?.member || null;
      const menuTabsBlocked = normalizeMenuTabsBlocked(member?.menuTabsBlocked ?? []);
      return {
        authUid: authUser.uid,
        ownerUid: contextData.ownerUid,
        role: normalizeMembershipRole(contextData.role || "staff"),
        status: normalizeMembershipStatus(contextData.status || "active"),
        isMember: !!contextData.isMember,
        member,
        menuTabsBlocked,
      };
    }
  } catch (error) {
    console.warn("[accountContext] Callable context resolution failed, falling back to Firestore lookup.", error);
  }

  try {
    return await resolveFromFirestore(authUser.uid);
  } catch (error) {
    console.warn("[accountContext] Firestore fallback failed, defaulting to authenticated user context.", error);
    return {
      authUid: authUser.uid,
      ownerUid: authUser.uid,
      role: "owner",
      status: "active",
      isMember: false,
      member: null,
      menuTabsBlocked: [],
    };
  }
};

export const applyAccountContextToStore = ({ authUser, context, store }) => {
  if (!authUser || !context || !store) return;

  store.state.authUser = {
    uid: authUser.uid,
    email: authUser.email || "",
    displayName: authUser.displayName || "",
    photoURL: authUser.photoURL || "",
    emailVerified: !!authUser.emailVerified,
    providerData: authUser.providerData || [],
  };

  store.state.accountOwnerId = context.ownerUid;
  store.state.accountContext = context;
  store.state.accountMemberMenuTabsBlocked = context.isMember
    ? normalizeMenuTabsBlocked(context.menuTabsBlocked)
    : [];

  const memberPartner = context.member?.partnerPricing;
  store.state.accountMemberPartnerPricing =
    memberPartner && typeof memberPartner === "object" ? memberPartner : null;

  // Keep existing code paths working by mapping currentUser.uid to ownerUid.
  store.state.currentUser = {
    uid: context.ownerUid,
    authUid: authUser.uid,
    email: authUser.email || "",
    displayName: authUser.displayName || "",
    photoURL: authUser.photoURL || "",
    emailVerified: !!authUser.emailVerified,
    providerData: authUser.providerData || [],
    isAccountMember: context.isMember,
    accountRole: context.role,
    accountStatus: context.status,
  };
};
